import React, { useMemo } from 'react';
import clsx from 'clsx';
import RttIcon from '@mui/icons-material/Rtt';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';

import { Empty } from '../../../components';
import { author_type, message_type } from '../utils/const';
import { getMessageBoxVariantClasses } from '../utils/utils';
import type { MessageResponse } from '../hooks/useConversation';
import { formatDate } from '../../conversations/utils';

const sortMessagesByTimestamp = (
  messages: MessageResponse[]
): MessageResponse[] => {
  return messages
    ? messages.sort(
        (a, b) =>
          new Date(a.timestamp!).getTime() - new Date(b.timestamp!).getTime()
      )
    : [];
};

type MessageHeaderProps = {
  isUser: boolean;
  icon?: React.ReactNode;
  messageType?: message_type;
};

const MessageHeader: React.FC<MessageHeaderProps> = ({
  isUser,
  icon,
  messageType,
}) => {
  const isTextType = messageType === null || messageType === message_type.TEXT;

  return (
    <div
      className={clsx(
        'text-medium font-semibold mb-xs flex items-center',
        isUser ? 'text-right justify-end mt-lg' : 'justify-start'
      )}
    >
      {isUser && (
        <div
          className="mr-2 mb-2"
          style={{ transform: isUser && !isTextType ? 'scaleX(-1)' : '' }}
        >
          {icon}
        </div>
      )}
      <span>{isUser ? 'User' : 'Agent'}</span>
      {!isUser && <div className="ml-2 mb-2">{icon}</div>}
    </div>
  );
};

type MessageBoxProps = {
  authorType: string | undefined;
  message: MessageResponse;
};

const MessageBox: React.FC<MessageBoxProps> = ({ authorType, message }) => {
  return (
    <div
      className={clsx(
        getMessageBoxVariantClasses(authorType, message.message_type),
        'text-medium p-lg rounded-slug border-2 overflow-y-auto w-full'
      )}
    >
      {message.message_text}
    </div>
  );
};

type MessagesProps = {
  className?: string;
  messages: MessageResponse[];
};

export const Messages: React.FC<MessagesProps> = ({
  className,
  messages: defaultMessages,
}) => {
  let prevMessage: MessageResponse | null = null;
  const messages = useMemo(
    () => sortMessagesByTimestamp(defaultMessages),
    [defaultMessages]
  );

  const messageIcon = (messageType?: string) => {
    return messageType === message_type.VOICE ? (
      <RecordVoiceOverIcon color="info" fontSize="large" />
    ) : (
      <RttIcon color="primary" fontSize="large" />
    );
  };

  return (
    <div
      className={clsx(className, 'flex flex-col gap-y-md pr-lg min-w-[20rem]')}
    >
      {messages.length === 0 ? (
        <Empty message="No messages" />
      ) : (
        messages
          .filter(message => !!message.message_text?.trim?.()?.length)
          .map((message, index) => {
            const isUser = message.author_type === author_type.USER;
            const showHeader = prevMessage?.author_type !== message.author_type;

            prevMessage = message;

            return (
              <div
                key={message.id ?? index}
                className={clsx(
                  'max-w-[80%] group/message-group',
                  isUser ? 'ml-auto' : ''
                )}
              >
                {showHeader && (
                  <MessageHeader
                    isUser={isUser}
                    icon={messageIcon(message?.message_type)}
                    messageType={message?.message_type}
                  />
                )}
                <div
                  className={clsx(
                    'text-small italic font-medium pl-md mt-md',
                    !isUser ? 'ml-sm' : ''
                  )}
                >
                  {formatDate(message.timestamp)}
                </div>
                <MessageBox
                  authorType={message.author_type}
                  message={message}
                />
              </div>
            );
          })
      )}
    </div>
  );
};
